import React from "react";
import styled from "styled-components";
import RippleEffect from "../../RippleEffect/RippleEffect";
import { RippleEffectShapeEnum } from "../../RippleEffect/RippleEffectShapeEnum";
import Icon from "../../Icon/Icon";
import { ThemeColorEnum, ThemeIconEnum } from "../../Themes/ThemeEnums";
import { ITheme } from "../../Themes/ThemeTypes";

const Button = styled.button<{ theme: ITheme; $isDisabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-inline-size: 48px;
  height: 40px;
  border: none;
  outline: none;
  background: transparent;
  color: ${(props) =>
    props.$isDisabled
      ? `rgba(${props.theme.colors.on_surface}, ${props.theme.base.opacity.disabled_content})`
      : `rgb(${props.theme.colors.primary})`};
  font-family: ${(props) => props.theme.base.typography.font};
  font-size: ${(props) => props.theme.base.typography.label.large.font_size}px;
  font-weight: ${(props) => props.theme.base.typography.label.large.font_weight};
  letter-spacing: ${(props) => props.theme.base.typography.label.large.letter_spacing}px;
  gap: 8px;
  padding: 0 12px;
  border-radius: ${(props) => props.theme.base.button.radius};
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  cursor: ${(props) => (props.$isDisabled ? "not-allowed" : "pointer")};
  user-select: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background-color: rgb(${(props) => props.theme.colors.primary});
    border-radius: ${(props) => props.theme.base.button.radius};
    opacity: 0;
    transition: opacity ${(props) => props.theme.base.motion.duration.short4}ms
      ${(props) => props.theme.base.motion.easing.standard};
  }

  &:hover:before {
    opacity: ${(props) => (props.$isDisabled ? 0 : props.theme.base.opacity.hover_state_layer)};
  }
`;

interface TextButtonProps {
  label: string;
  icon?: ThemeIconEnum;
  disabled?: boolean;
  onClick?: () => void;
}

const TextButton: React.FC<TextButtonProps> = ({ label, icon, disabled, onClick }) => {
  const handleClick = (): void => {
    if (!disabled && onClick) onClick();
  };
  return (
    <RippleEffect shape={RippleEffectShapeEnum.BUTTON}>
      <Button $isDisabled={disabled} onClick={handleClick} type="button">
        {icon !== undefined && <Icon icon={icon} color={ThemeColorEnum.PRIMARY} />}
        {label}
      </Button>
    </RippleEffect>
  );
};

export default TextButton;
